import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { isViewerFromStorage } from '../utils/staffAccess';
import { orderTrackingWasSent, saveOrderTracking } from '../utils/orderTracking';
import './TrackingNumberModal.css';

const formatSentAt = (value) => {
  if (!value) return '';
  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleString('es-AR');
  }
  if (value.seconds) {
    return new Date(value.seconds * 1000).toLocaleString('es-AR');
  }
  const asDate = new Date(value);
  return Number.isNaN(asDate.getTime()) ? '' : asDate.toLocaleString('es-AR');
};

const TrackingNumberModal = ({ item, source, onClose, showMessage, onSaved }) => {
  const isViewer = isViewerFromStorage();
  const [trackingNumber, setTrackingNumber] = useState(String(item?.trackingNumber || ''));
  const [trackingLink, setTrackingLink] = useState(String(item?.trackingLink || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sent = orderTrackingWasSent(item);
  const pending = !!(item?.sendTrackingEmail && !sent);
  const orderLabel = item?.orderNumber || (item?.id ? item.id.slice(0, 6) : '');
  const sentAtLabel = formatSentAt(item?.trackingEmailSentAt);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  const handleSave = async () => {
    if (isViewer || !item?.id) return;
    setError('');
    setSaving(true);
    try {
      const saved = await saveOrderTracking(source, item.id, trackingNumber, trackingLink);
      showMessage?.('Seguimiento guardado. El email se envía automáticamente.');
      onSaved?.({
        ...saved,
        trackingSent: false,
        sendTrackingEmail: true,
        trackingEmailError: ''
      });
      onClose();
    } catch (err) {
      console.error('Error guardando seguimiento:', err);
      const message = err.message || 'Error al guardar el seguimiento.';
      setError(message);
      showMessage?.(message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tnm-overlay" role="dialog" aria-modal="true" aria-labelledby="tnm-title">
      <div className="tnm-content">
        <div className="tnm-header">
          <h3 id="tnm-title">Seguimiento {orderLabel ? `· #${orderLabel}` : ''}</h3>
          <button
            type="button"
            className="tnm-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={saving}
          >
            ✕
          </button>
        </div>

        {sent && (
          <p className="tnm-status is-sent">
            Número ya enviado al cliente
            {item.trackingNumber ? ` · ${item.trackingNumber}` : ''}
            {sentAtLabel ? ` · ${sentAtLabel}` : ''}
          </p>
        )}
        {!sent && pending && (
          <p className="tnm-status is-pending">El email de seguimiento se está enviando…</p>
        )}
        {!sent && item?.trackingEmailError && (
          <p className="tnm-status is-error">Error al enviar: {item.trackingEmailError}</p>
        )}
        {!sent && !pending && item?.trackingNumber && (
          <p className="tnm-status is-loaded">Número cargado, pendiente de envío: {item.trackingNumber}</p>
        )}

        <label className="tnm-field">
          Número de seguimiento
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Ej: AR123456789"
            disabled={saving || isViewer}
            autoFocus={!isViewer}
          />
        </label>
        <label className="tnm-field">
          Link de seguimiento
          <input
            type="url"
            value={trackingLink}
            onChange={(e) => setTrackingLink(e.target.value)}
            placeholder="https://correoargentino.com.ar/seguimiento"
            disabled={saving || isViewer}
          />
        </label>

        <p className="tnm-hint">
          Al guardar se dispara el email automático al cliente con el número y el link.
        </p>
        {error ? <p className="tnm-error">{error}</p> : null}

        <div className="tnm-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            {isViewer ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isViewer && (
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
              disabled={saving || !trackingNumber.trim() || !trackingLink.trim()}
            >
              {saving ? 'Guardando…' : (sent ? 'Actualizar y reenviar' : 'Guardar y enviar')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TrackingNumberModal;
